import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import concat from "lodash/fp/concat";
import find from "lodash/fp/find";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import isEmpty from "lodash/fp/isEmpty";
import set from "lodash/fp/set";
import uniq from "lodash/fp/uniq";

import * as api from "api";
import { transformRecipeData } from "utils";
import selectSelectedProduct from "./selectSelectedProduct";
import createRecipeAndImage from "api/createRecipeAndImage";
import getSelectedProductStateValues from "./getSelectedProductStateValues";
import getNextUserChoiceValues from "./getNextUserChoiceValues";

export const fetchProductFormData = createAsyncThunk(
  "app/fetchProductFormData",
  ({ productFormDataUrl }, { signal }) => {
    return api.fetchProductFormData({ signal })({ productFormDataUrl });
  }
);

export const fetchRecipe = createAsyncThunk(
  "app/fetchRecipe",
  ({ recipeID }, { signal }) => {
    return api.fetchRecipe({ signal })({ recipeID });
  }
);

const addToOrderFn = ({ productSku, recipeID, userChoiceValues }) => {
  if (window[process.env.REACT_APP_ADD_TO_ORDER_FUNCTION]) {
    window[process.env.REACT_APP_ADD_TO_ORDER_FUNCTION].call(
      null,
      productSku,
      recipeID,
      userChoiceValues
    );
  } else {
    console.warn("missing addToOrder function");
    console.log({ productSku, recipeID, userChoiceValues });
  }
};

export const addToOrder = createAsyncThunk(
  "app/addToOrder",
  ({ images, productSku, ...userChoiceValues }, { signal }) => {
    return createRecipeAndImage({
      userChoiceValues,
      signal,
      images,
    }).then(({ recipeID, finalImages }) => {
      addToOrderFn({
        productSku,
        recipeID,
        userChoiceValues: {
          ...userChoiceValues,
          final_image: finalImages[0].finalImage,
        },
      });
    });
  }
);

function getCustomisationStage(stageNumber) {
  switch (stageNumber) {
    case 2:
      return {
        step1: { isInProgress: true, isComplete: true },
        step2: { isInProgress: true, isComplete: false },
        step3: { isInProgress: false, isComplete: false },
        step4: { isInProgress: false, isComplete: false },
      };
    case 3:
      return {
        step1: { isInProgress: true, isComplete: true },
        step2: { isInProgress: true, isComplete: true },
        step3: { isInProgress: true, isComplete: false },
        step4: { isInProgress: false, isComplete: false },
      };
    case 4:
      return {
        step1: { isInProgress: true, isComplete: true },
        step2: { isInProgress: true, isComplete: true },
        step3: { isInProgress: true, isComplete: true },
        step4: { isInProgress: true, isComplete: false },
      };

    default:
      return {
        step1: { isInProgress: true, isComplete: false },
        step2: { isInProgress: false, isComplete: false },
        step3: { isInProgress: false, isComplete: false },
        step4: { isInProgress: false, isComplete: false },
      };
  }
}

export const localeToCurrencyMap = {
  "en-US": "USD",
  "en-CA": "CAD",
  "en-GB": "GBP",
  "en-SE": "KRR",
  "en-FI": "EUR",
  "en-DK": "DKK",
  "fr-CA": "CAD",
  "fr-FR": "EUR",
  "de-DE": "EUR",
};

export const initialState = {
  locale: "en_US",
  currency: "USD",
  selectedProductId: "",
  selectedCustomisationId: "",
  selectedImageAngle: "",
  formData: {},
  userChoiceValues: {},
  fieldsTouched: [],
  customisationsVisited: [],
  modals: [],
  volumentalData: {},
  dismissedNotificationIds: [],
  isAddingProductToOrder: false,
  customisationStage: getCustomisationStage(1),
  loadedRecipe: {},
  showReviewOrderPage: false,
  customisationCompleteModalShown: false,
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setProductId(state, action) {
      if (state.selectedProductId === action.payload) {
        return;
      }

      const hasVolumentalData = !isEmpty(state.volumentalData);

      const selectedProduct = flow(
        current,
        getOr([], ["formData", "products"]),
        find(({ id }) => id === action.payload)
      )(state);

      const {
        userChoiceValues,
        selectedProductId,
        selectedCustomisationId,
        customisationsVisited,
        fieldsTouched,
        selectedImageAngle,
      } = getSelectedProductStateValues({
        hasVolumentalData,
      })(selectedProduct);

      state.userChoiceValues = userChoiceValues;
      state.selectedProductId = selectedProductId;
      state.selectedCustomisationId = selectedCustomisationId;
      state.customisationsVisited = customisationsVisited;
      state.fieldsTouched = fieldsTouched;
      state.selectedImageAngle = selectedImageAngle;
      state.customisationStage = getCustomisationStage(action.payload ? 2 : 1);

      // Checks whether to display "SelectGoalGearModal" for GoalieGear only
      if (
        state.formData.id === "goalie-gear" &&
        selectedProductId &&
        !state.customisationsVisited.includes("select-goal-gear")
      ) {
        const customisation = selectedProduct.customisations.find(
          ({ id }) => id === "select-goal-gear"
        );

        state.modals.push({
          key: state.modals.length,
          modal: "SelectGoalGearModal",
          modalProps: { customisation },
        });

        state.customisationsVisited.push("select-goal-gear");
      }

      if (
        (state.formData.id === "skates" &&
          selectedProductId === "SupremeMachSkate2022" &&
          !state.customisationsVisited.includes(
            "UltrasonicSkate2021__boot-length"
          )) ||
        (state.formData.id === "skates" &&
          selectedProductId === "Hyperlite2Skate2023" &&
          !state.customisationsVisited.includes(
            "Hyperlite2Skate2023__boot-length"
          ))
      ) {
        state.modals.push({
          key: state.modals.length,
          modal: "SelectSkateOrderGuidance",
          modalProps: {},
        });
        state.customisationsVisited.push("select-skate");
      }
    },

    setCustomisationId(state, action) {
      state.selectedCustomisationId = action.payload;
      state.customisationsVisited = flow(
        concat(action.payload),
        uniq
      )(state.customisationsVisited);

      const imageAngle = flow(
        current,
        getOr([], ["formData", "products"]),
        find(({ id }) => id === state.selectedProductId),
        getOr([], ["customisations"]),
        find(({ id }) => id === action.payload),
        get("imageAngle")
      )(state);

      if (imageAngle) {
        state.selectedImageAngle = imageAngle;
      }
    },

    setCustomisationStage(state, action) {
      state.customisationStage = getCustomisationStage(action.payload);
    },

    setSelectedImageAngle(state, action) {
      state.selectedImageAngle = action.payload;
    },

    setTouchedField(state, action) {
      state.fieldsTouched = uniq([...state.fieldsTouched, action.payload]);
    },

    setShowReviewOrderPage(state, { payload }) {
      state.showReviewOrderPage = payload;
    },

    setCustomisationCompleteModalShown(state, { payload }) {
      state.customisationCompleteModalShown = payload;
    },

    clearLoadedRecipe(state) {
      state.loadedRecipe = {};
    },

    updateUserChoiceValues(state, action) {
      const product = selectSelectedProduct({ app: current(state) });
      const userChoiceValues = current(state).userChoiceValues;

      state.userChoiceValues = getNextUserChoiceValues({
        product,
        userChoiceValues,
      })(action);
    },

    displayModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: action.payload.modal,
        modalProps: action.payload?.modalProps || {},
      });
    },

    displayReviewModal(state) {
      state.modals.push({
        key: state.modals.length,
        modal: "ReviewModal",
        modalProps: {},
      });
    },

    displayDetailsModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "DetailsModal",
        modalProps: action.payload || {},
      });
    },

    displayCompareModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "CompareModal",
        modalProps: action.payload || {},
      });
    },

    displaySaveModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "SaveModal",
        modalProps: action.payload?.modalProps || {},
      });
    },

    displayShareModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "ShareModal",
        modalProps: action.payload?.modalProps || {},
      });
    },

    displayFullscreenSelectionModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "ModalSelectProCustomGraphic",
        modalProps: action.payload || {},
      });
      state.customisationsVisited.push(action.payload?.id);
    },

    displayCustomisationsCompleteModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "CustomisationsCompleteModal",
        modalProps: action.payload || {},
      });
    },

    displayInfoModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "InfoModal",
        modalProps: action.payload || {},
      });
    },

    displayVideoModal(state, action) {
      state.modals.push({
        key: state.modals.length,
        modal: "VideoModal",
        modalProps: action.payload || {},
      });
    },

    closeModal(state, action) {
      state.modals = state.modals.filter(
        ({ key }) => key !== action.payload.modalKey
      );
    },

    closeModals(state) {
      state.modals = [];
    },

    dismissNotification(state, action) {
      state.dismissedNotificationIds.push(action.payload);
    },

    setFormData(state, { payload }) {
      const {
        formData,
        family,
        recipeData = {},
        initialUserChoiceValues = {},
      } = payload;

      let volumentalData = payload?.volumentalData ?? {};

      if (recipeData["Player Name, First"] || recipeData["Player Name, Last"]) {
        volumentalData = set(
          ["user_profile", "name"],
          [recipeData["Player Name, First"], recipeData["Player Name, Last"]]
            .filter(Boolean)
            .join(" ")
        )(volumentalData);
      }
      if (recipeData["Unique Foot Scan ID"]) {
        volumentalData = set(
          ["scan", "external_id"],
          recipeData["Unique Foot Scan ID"]
        )(volumentalData);
      }
      if (recipeData["Vandra Scanner Number"]) {
        volumentalData = set(
          ["scan", "device_id"],
          recipeData["Vandra Scanner Number"]
        )(volumentalData);
      }

      const hasVolumentalData = !isEmpty(volumentalData);

      const selectedProduct = flow(getOr([], ["products"]), (products) => {
        if (recipeData?.ProductFamily) {
          const foundProduct = products.find(
            ({ productFamily }) => productFamily === recipeData?.ProductFamily
          );
          if (foundProduct) {
            return foundProduct;
          }
        }

        if (products.length === 1) {
          return products[0];
        }

        if (family) {
          return find((product) => product.id === family)(products);
        }

        return null;
      })(formData);

      if (selectedProduct) {
        const initialUserChoiceValuesWithRecipeData = transformRecipeData({
          initialUserChoiceValues,
          recipeData,
          productFamily: selectedProduct.productFamily,
          productCustomisations: selectedProduct.customisations,
        });

        const {
          userChoiceValues,
          selectedProductId,
          selectedCustomisationId,
          customisationsVisited,
          fieldsTouched,
          selectedImageAngle,
        } = getSelectedProductStateValues({
          hasVolumentalData,
          initialUserChoiceValues: initialUserChoiceValuesWithRecipeData,
        })(selectedProduct);

        state.userChoiceValues = userChoiceValues;
        state.selectedProductId = selectedProductId;
        state.selectedCustomisationId = selectedCustomisationId;
        state.customisationsVisited = customisationsVisited;
        state.fieldsTouched = fieldsTouched;
        state.selectedImageAngle = selectedImageAngle;
        state.customisationStage = getCustomisationStage(2);

        // Checks whether to display "SelectGoalGearModal" for GoalieGear only
        if (
          formData.id === "goalie-gear" &&
          selectedProductId &&
          !customisationsVisited.includes("select-goal-gear")
        ) {
          const customisation = selectedProduct.customisations.find(
            ({ id }) => id === "select-goal-gear"
          );
          state.modals.push({
            key: state.modals.length,
            modal: "SelectGoalGearModal",
            modalProps: { customisation },
          });
          state.customisationsVisited.push("select-goal-gear");
        }
      }

      state.volumentalData = volumentalData;
      state.formData = formData;
    },
  },

  extraReducers(builder) {
    builder.addCase(addToOrder.pending, (state) => {
      state.isAddingProductToOrder = true;
    });

    builder.addCase(addToOrder.rejected, (state) => {
      state.isAddingProductToOrder = false;
    });

    builder.addCase(fetchRecipe.fulfilled, (state, action) => {
      const recipeData = action.payload;
      const { formData } = current(state);

      let volumentalData = action.meta.arg.volumentalData ?? {};
      if (recipeData["Player Name, First"] || recipeData["Player Name, Last"]) {
        volumentalData = set(
          ["user_profile", "name"],
          [recipeData["Player Name, First"], recipeData["Player Name, Last"]]
            .filter(Boolean)
            .join(" ")
        )(volumentalData);
      }
      if (recipeData["Unique Foot Scan ID"]) {
        volumentalData = set(
          ["scan", "external_id"],
          recipeData["Unique Foot Scan ID"]
        )(volumentalData);
      }
      if (recipeData["Vandra Scanner Number"]) {
        volumentalData = set(
          ["scan", "device_id"],
          recipeData["Vandra Scanner Number"]
        )(volumentalData);
      }
      const hasVolumentalData = !isEmpty(volumentalData);

      const selectedProduct = flow(
        getOr([], ["products"]),
        find(({ productFamily }) => productFamily === recipeData?.ProductFamily)
      )(formData);

      if (selectedProduct) {
        const userChoiceValues = transformRecipeData({
          recipeData,
          productFamily: selectedProduct.productFamily,
          productCustomisations: selectedProduct.customisations,
        });

        const {
          selectedProductId,
          selectedCustomisationId,
          customisationsVisited,
          selectedImageAngle,
        } = getSelectedProductStateValues({
          hasVolumentalData,
          initialUserChoiceValues: userChoiceValues,
        })(selectedProduct);

        state.selectedProductId = selectedProductId;
        state.volumentalData = volumentalData;
        state.selectedCustomisationId = selectedCustomisationId;
        state.customisationsVisited = customisationsVisited;
        state.selectedImageAngle = selectedImageAngle;
        state.userChoiceValues = userChoiceValues;
        state.customisationStage = getCustomisationStage(2);
        state.loadedRecipe = {
          loadedRecipeID: recipeData?.recipeID,
          webAppValues: JSON.parse(recipeData.webAppValues),
        };
      }
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  clearLoadedRecipe,
  setFormData,
  setProductId,
  setCustomisationId,
  setCustomisationStage,
  setSelectedImageAngle,
  setTouchedField,
  setShowReviewOrderPage,
  setCustomisationCompleteModalShown,
  updateUserChoiceValues,
  displayModal,
  displayReviewModal,
  displaySaveModal,
  displayShareModal,
  displayFullscreenSelectionModal,
  displayInfoModal,
  displayCompareModal,
  displayVideoModal,
  displayDetailsModal,
  displayCustomisationsCompleteModal,
  closeModal,
  closeModals,
  dismissNotification,
} = appSlice.actions;

export default appSlice.reducer;
